// Globaler Scan: Ein gescannter Code wird einer Art zugeordnet (Artikel-Barcode, Belegnummer, Charge …) und an
// die Scan-Ziele der Module verteilt (scanHandlers im Manifest). Reine Funktionen; der Dialog steht in ScanDialog.tsx.
import { passesGate } from '../api/permissions'
import type { SessionPayload } from '../api/types'
import type { ScanResult } from '../lib/scan'
import type { ScanHandler, WebModule } from '../module'

type Kind = ScanHandler['match']

const ALL_KINDS: readonly Kind[] = ['item-barcode', 'document-number', 'lot', 'location', 'bin']

/** Welche Arten kommen für diesen Code in Frage? Eindeutige Formen zuerst, sonst alle. */
export function classifyScan(result: Pick<ScanResult, 'code' | 'format' | 'gs1'>): Kind[] {
  const code = result.code.trim()
  if (result.gs1?.gtin) return result.gs1.lot ? ['item-barcode', 'lot'] : ['item-barcode']
  if (result.format === 'ean_13' || result.format === 'ean_8' || result.format === 'upc_a' || result.format === 'itf') {
    return ['item-barcode']
  }
  if (/^\d{8}$|^\d{12,14}$/.test(code)) return ['item-barcode']
  // Belegnummern der Nummernkreise: Kürzel-Jahr-Zähler (AU-2026-01482, WE-2026-0291)
  if (/^[A-ZÄÖÜ]{1,5}-\d{2,4}-\d{2,}$/i.test(code)) return ['document-number']
  return [...ALL_KINDS]
}

export interface ScanTarget {
  key: string
  label: string
  to: string
}

/** Ziele für einen Code: Scan-Ziele aller Module, die zur Art passen und für die das Recht besteht. */
export function resolveScan(
  result: Pick<ScanResult, 'code' | 'format' | 'gs1'>,
  modules: readonly WebModule[],
  session: Pick<SessionPayload, 'permissions' | 'activeLocationId' | 'tenant'> | null,
): ScanTarget[] {
  const kinds = classifyScan(result)
  const targets: ScanTarget[] = []
  for (const module of modules) {
    module.scanHandlers.forEach((handler, index) => {
      if (!kinds.includes(handler.match)) return
      if (!passesGate(session, { permission: handler.permission, feature: handler.feature }, 'any')) return
      // GS1: der Artikel steckt in der GTIN, die Charge im Datenbezeichner 10 – dann steht die Art des Codes fest
      const element = handler.match === 'item-barcode' ? result.gs1?.gtin : handler.match === 'lot' ? result.gs1?.lot : undefined
      const code = element ?? result.code.trim()
      if (handler.accepts && !handler.accepts(code, { gs1: element !== undefined, format: result.format })) return
      targets.push({ key: `${module.id}:${index}`, label: handler.label, to: handler.to(code) })
    })
  }
  return targets
}
